import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FiPlus,
  FiFileText,
  FiEye,
  FiSearch,
  FiBox,
  FiChevronLeft,
  FiChevronRight,
  FiArrowLeft,
  FiPackage,
  FiCalendar,
  FiMapPin,
  FiCheckCircle,
  FiXCircle,
  FiImage,
  FiVideo,
  FiDollarSign,
  FiTag,
  FiExternalLink,
  FiClock,
} from "react-icons/fi";
import toast from "react-hot-toast";
import axios from "../../Utils/axiosInstance";
import AppLoader from "../../Components/Common/AppLoader";

const STATUS_TABS = [
  { key: "ALL", label: "All" },
  { key: "Under_Review", label: "Under Review" },
  { key: "Approved", label: "Approved" },
  { key: "Offer_Created", label: "Offer Created" },
  { key: "Rejected", label: "Rejected" },
  { key: "Expired", label: "Expired" },
  { key: "Cancelled", label: "Cancelled" },
];

const getStatusBadge = (status) => {
  switch (status) {
    case "Approved":
      return {
        label: "Approved",
        bg: "bg-emerald-50 text-emerald-700 border-emerald-200",
        bannerBg: "bg-emerald-500/20 text-emerald-200 border-emerald-400/30",
      };
    case "Rejected":
      return {
        label: "Rejected",
        bg: "bg-red-50 text-red-700 border-red-200",
        bannerBg: "bg-red-500/20 text-red-200 border-red-400/30",
      };
    case "Offer_Created":
      return {
        label: "Offer Created",
        bg: "bg-blue-50 text-blue-700 border-blue-200",
        bannerBg: "bg-blue-500/20 text-blue-200 border-blue-400/30",
      };
    case "Expired":
      return {
        label: "Expired",
        bg: "bg-orange-50 text-orange-700 border-orange-200",
        bannerBg: "bg-orange-500/20 text-orange-200 border-orange-400/30",
      };
    case "Cancelled":
      return {
        label: "Cancelled",
        bg: "bg-gray-50 text-gray-700 border-gray-200",
        bannerBg: "bg-gray-500/20 text-gray-200 border-gray-400/30",
      };
    default:
      return {
        label: "Under Review",
        bg: "bg-amber-50 text-amber-700 border-amber-200",
        bannerBg: "bg-amber-500/20 text-amber-200 border-amber-400/30",
      };
  }
};

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch (error) {
    return "—";
  }
};

const formatCurrency = (val) => {
  if (val === null || val === undefined || val === "") return "—";
  return `₹${Number(val).toLocaleString("en-IN")}`;
};

const SupplierRQMRequests = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialRqmId = searchParams.get("rqmId");

  const [rqms, setRqms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedRqm, setSelectedRqm] = useState(null);
  const [activeMedia, setActiveMedia] = useState(null);

  // Pagination states
  const [page, setPage] = useState(1);
  const [limit] = useState(12);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const fetchRQMs = useCallback(async () => {
    try {
      setLoading(true);
      let url = `/rqm/supplier/my-rqms?page=${page}&limit=${limit}`;
      if (activeTab !== "ALL") {
        url += `&status=${activeTab}`;
      }
      const res = await axios.get(url);
      const list = res?.data?.data || [];
      setRqms(list);

      if (res?.data?.pagination) {
        setTotalPages(res.data.pagination.pages || 1);
        setTotalItems(res.data.pagination.total || list.length);
      } else {
        setTotalPages(1);
        setTotalItems(list.length);
      }

      // Auto-select RQM based on query parameter or select first item
      if (list.length > 0) {
        if (initialRqmId) {
          const found = list.find((item) => item._id === initialRqmId);
          setSelectedRqm(found || list[0]);
        } else {
          setSelectedRqm((prev) => {
            if (prev) {
              const stillExists = list.find((i) => i._id === prev._id);
              return stillExists || list[0];
            }
            return list[0];
          });
        }
      } else {
        setSelectedRqm(null);
      }
    } catch (err) {
      console.error("Failed to fetch supplier RQMs:", err);
      toast.error(err?.response?.data?.message || "Failed to load RQM requests");
      setRqms([]);
      setSelectedRqm(null);
    } finally {
      setLoading(false);
    }
  }, [page, limit, activeTab, initialRqmId]);

  useEffect(() => {
    fetchRQMs();
  }, [fetchRQMs]);

  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setPage(1);
  };

  const filteredRqms = rqms.filter((item) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const rqmNum = (item.rqmNumber || "").toLowerCase();
    const prodName = (item.product?.name || "").toLowerCase();
    const varietyName = (item.productVariety?.varietyName || item.customVarietyName || "").toLowerCase();
    const catName = (item.category?.name || "").toLowerCase();
    return (
      rqmNum.includes(query) ||
      prodName.includes(query) ||
      varietyName.includes(query) ||
      catName.includes(query)
    );
  });

  return (
    <div className="w-full bg-[#F8FAFC] min-h-screen py-6 px-4 md:px-8">
      <div className="max-w-7xl mx-auto space-y-6">

        {/* HEADER SECTION */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white p-5 md:p-6 rounded-2xl border border-gray-200 shadow-2xs">
          <div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primaryColor/10 text-primaryColor text-xs font-bold mb-2">
              <FiFileText size={14} /> My RQM Requests
            </div>
            <h1 className="text-2xl md:text-3xl font-extrabold text-gray-900 tracking-tight">
              Supplier RQM Management
            </h1>
            <p className="text-xs md:text-sm text-gray-500 mt-1">
              View and manage your RQM requests. Tap on an order to see details, compare specifications, and track approval status.
            </p>
          </div>

          <button
            onClick={() => navigate("/post-rqm-request")}
            className="flex items-center justify-center gap-2 bg-primaryColor hover:bg-primaryColor/90 text-white font-semibold text-xs md:text-sm px-5 py-3 rounded-xl shadow-md transition duration-200 active:scale-95 shrink-0"
          >
            <FiPlus size={18} /> Post New RQM Request
          </button>
        </div>

        {/* MAIN SPLIT VIEW LAYOUT */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">

          {/* LEFT SIDEBAR: LIST OF RQM REQUESTS (4 COLUMNS ON DESKTOP) */}
          <div className="lg:col-span-5 xl:col-span-4 space-y-4 lg:sticky lg:top-20 lg:max-h-[calc(100vh-100px)] flex flex-col">

            {/* SEARCH AND STATUS TABS CONTAINER */}
            <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-2xs space-y-3 shrink-0">

              {/* SEARCH BAR */}
              <div className="flex items-center bg-gray-50 border border-gray-200 rounded-xl px-3 py-2 focus-within:border-primaryColor focus-within:ring-2 focus-within:ring-primaryColor/10 transition">
                <FiSearch className="text-gray-400 mr-2 flex-shrink-0" size={16} />
                <input
                  type="text"
                  placeholder="Search by RQM #, product..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="bg-transparent outline-none text-xs w-full text-gray-800 placeholder-gray-400"
                />
              </div>

              {/* STATUS TABS */}
              <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
                {STATUS_TABS.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => handleTabChange(tab.key)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap transition-all duration-200 border ${activeTab === tab.key
                        ? "bg-primaryColor text-white border-primaryColor shadow-2xs"
                        : "bg-gray-50 text-gray-600 border-gray-200 hover:bg-gray-100"
                      }`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            </div>

            {/* RQM LIST CARDS */}
            {loading ? (
              <div className="bg-white border border-gray-200 rounded-2xl p-8 flex items-center justify-center min-h-[300px]">
                <AppLoader fullScreen={false} message="Loading RQM requests..." />
              </div>
            ) : filteredRqms.length === 0 ? (
              <div className="bg-white border border-dashed border-gray-300 rounded-2xl p-8 text-center space-y-3">
                <div className="w-12 h-12 mx-auto rounded-full bg-gray-100 flex items-center justify-center text-gray-400">
                  <FiBox size={24} />
                </div>
                <h3 className="text-sm font-bold text-gray-800">No RQM Requests Found</h3>
                <p className="text-xs text-gray-500 max-w-xs mx-auto">
                  {searchQuery
                    ? "No requests match your search."
                    : "No RQM requests found in this status."}
                </p>
              </div>
            ) : (
              <div className="space-y-3 flex-1 overflow-y-auto pr-1 scrollbar-thin scrollbar-thumb-gray-200">
                {filteredRqms.map((item) => {
                  const badge = getStatusBadge(item.status);
                  const isSelected = selectedRqm?._id === item._id;
                  const categoryName = item.category?.name || "Category";
                  const productName = item.product?.name || "Product";
                  const varietyName =
                    item.productVariety?.varietyName || item.customVarietyName || "Variety";
                  const price = item.pricing?.amount
                    ? `₹${Number(item.pricing.amount).toLocaleString("en-IN")}`
                    : "—";
                  const unit = item.priceAndQuantity?.standardizedUnit || "Kg";
                  const firstImg = item.referencePhotoAndVideo?.images?.[0];

                  return (
                    <div
                      key={item._id}
                      onClick={() => setSelectedRqm(item)}
                      className={`relative rounded-2xl p-4 transition-all duration-200 cursor-pointer flex flex-col justify-between border ${isSelected
                          ? "bg-white border-primaryColor ring-2 ring-primaryColor/20 shadow-md"
                          : "bg-white border-gray-200 hover:border-gray-300 shadow-2xs hover:shadow-xs"
                        }`}
                    >
                      {/* CARD CONTENT HEADER */}
                      <div className="flex items-start gap-3">
                        {/* THUMBNAIL */}
                        <div className="w-14 h-14 rounded-xl border border-gray-200 bg-gray-50 overflow-hidden flex-shrink-0 flex items-center justify-center">
                          {firstImg ? (
                            <img src={firstImg} alt={productName} className="w-full h-full object-cover" />
                          ) : (
                            <FiBox size={22} className="text-gray-400" />
                          )}
                        </div>

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-1 mb-0.5">
                            <span className="text-[11px] font-bold text-primaryColor font-mono truncate">
                              {item.rqmNumber || "RQM"}
                            </span>
                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border shrink-0 ${badge.bg}`}>
                              {badge.label}
                            </span>
                          </div>

                          <h4 className="text-sm font-bold text-gray-900 truncate">
                            {productName}
                          </h4>
                          <p className="text-xs text-gray-500 truncate">
                            {varietyName}
                          </p>
                        </div>
                      </div>

                      {/* CARD METRICS */}
                      <div className="mt-3 pt-2.5 border-t border-gray-100 flex items-center justify-between text-xs">
                        <div className="flex items-center gap-3">
                          <span className="text-[11px] font-semibold text-gray-600">
                            Qty: <strong className="text-gray-900">{item.priceAndQuantity?.quantity || "—"} {unit}</strong>
                          </span>
                          <span className="text-[11px] font-semibold text-emerald-700">
                            Rate: <strong>{price}</strong>
                          </span>
                        </div>

                        <span className="text-[10px] text-gray-400 font-medium">
                          {formatDate(item.createdAt)}
                        </span>
                      </div>
                    </div>
                  );
                })}

                {/* PAGINATION BAR */}
                {totalPages > 1 && (
                  <div className="bg-white p-3 rounded-2xl border border-gray-200 shadow-2xs flex items-center justify-between gap-2 text-xs">
                    <button
                      onClick={() => setPage((prev) => Math.max(1, prev - 1))}
                      disabled={page === 1}
                      className="p-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed text-gray-700"
                    >
                      <FiChevronLeft size={16} />
                    </button>
                    <span className="text-gray-500 font-medium text-[11px]">
                      Page <strong>{page}</strong> of <strong>{totalPages}</strong>
                    </span>
                    <button
                      onClick={() => setPage((prev) => Math.min(totalPages, prev + 1))}
                      disabled={page === totalPages}
                      className="p-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed text-gray-700"
                    >
                      <FiChevronRight size={16} />
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* RIGHT MAIN PANEL: SELECTED RQM DETAILS (8 COLUMNS ON DESKTOP) */}
          <div className="lg:col-span-7 xl:col-span-8 space-y-6">
            {!selectedRqm ? (
              <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center space-y-3 shadow-2xs min-h-[400px] flex flex-col items-center justify-center">
                <FiBox size={40} className="text-gray-300" />
                <h3 className="text-base font-bold text-gray-800">Select an RQM Request</h3>
                <p className="text-xs text-gray-500 max-w-sm">
                  Click on any RQM request from the list on the left to view detailed specifications, packaging, price breakdown, and media attachments.
                </p>
              </div>
            ) : (
              <div className="space-y-6">

                {/* HERO BANNER CARD (MATCHING THE SCREENSHOT STYLE) */}
                <div className="relative bg-gradient-to-r from-[#701a31] via-[#881337] to-[#9f1239] text-white p-6 rounded-2xl shadow-md overflow-hidden space-y-4">
                  {/* BACKGROUND ACCENT PATTERN */}
                  <div className="absolute -right-10 -bottom-10 w-48 h-48 bg-white/5 rounded-full blur-2xl pointer-events-none" />

                  {/* TOP BANNER ROW */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-white/10 pb-4">
                    <div>
                      <div className="text-xs font-semibold text-rose-200 tracking-wide uppercase">
                        RQM Number: <span className="font-bold text-white font-mono">{selectedRqm.rqmNumber || "RQM"}</span>
                      </div>
                      <h2 className="text-xl sm:text-2xl font-extrabold text-white mt-1">
                        {selectedRqm.product?.name || "Product"}
                      </h2>
                      <p className="text-xs text-rose-100 font-medium">
                        Variety: {selectedRqm.productVariety?.varietyName || selectedRqm.customVarietyName || "Variety N/A"}
                        {selectedRqm.productVariety?.grade ? ` (Grade: ${selectedRqm.productVariety.grade})` : ""}
                      </p>
                    </div>

                    <div className="flex items-center gap-2 self-start sm:self-center">
                      <span className={`text-xs font-bold px-3 py-1 rounded-full border ${getStatusBadge(selectedRqm.status).bannerBg}`}>
                        {getStatusBadge(selectedRqm.status).label}
                      </span>
                    </div>
                  </div>

                  {/* BANNER METRICS ROW */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-xs pt-1">
                    <div>
                      <span className="text-[11px] text-rose-200 block">Offered Quantity</span>
                      <span className="text-base font-extrabold text-white">
                        {selectedRqm.priceAndQuantity?.quantity || 0} {selectedRqm.priceAndQuantity?.standardizedUnit || "Kg"}
                      </span>
                    </div>

                    <div>
                      <span className="text-[11px] text-rose-200 block">Offered Price</span>
                      <span className="text-base font-extrabold text-emerald-300">
                        {formatCurrency(selectedRqm.pricing?.amount)} / {selectedRqm.priceAndQuantity?.standardizedUnit || "Kg"}
                      </span>
                    </div>

                    <div>
                      <span className="text-[11px] text-rose-200 block">Submitted On</span>
                      <span className="text-xs font-bold text-white">
                        {formatDate(selectedRqm.timeline?.submittedAt || selectedRqm.createdAt)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* STATUS NOTIFICATIONS */}
                {selectedRqm.status === "Rejected" && (
                  <div className="bg-red-50 border border-red-200 p-4 rounded-2xl flex items-start gap-3 text-red-800 text-xs shadow-2xs">
                    <FiXCircle size={20} className="flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold">RQM Request Rejected</p>
                      <p className="mt-1">{selectedRqm.adminReview?.reviewNote || "No rejection reason provided."}</p>
                    </div>
                  </div>
                )}

                {selectedRqm.status === "Offer_Created" && (
                  <div className="bg-blue-50 border border-blue-200 p-4 rounded-2xl flex items-center justify-between gap-3 text-blue-900 text-xs shadow-2xs">
                    <div className="flex items-center gap-2">
                      <FiCheckCircle size={20} className="text-blue-600 flex-shrink-0" />
                      <div>
                        <p className="font-bold">Offer Created from this RQM Request!</p>
                        <p className="text-[11px] text-blue-700">
                          Unique Offer ID: <strong className="font-mono">{selectedRqm.linkedOffer?.offerId?.offerUniqueNumber || "OFR"}</strong>
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* PRODUCT & PRICING BREAKDOWN CARD */}
                <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-2xs space-y-4">
                  <h3 className="text-sm font-extrabold text-gray-900 flex items-center gap-2 border-b border-gray-100 pb-3">
                    <FiBox className="text-primaryColor" /> Product & Pricing Overview
                  </h3>

                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-xs">
                    <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                      <p className="text-[10px] font-bold text-gray-400 uppercase">Category</p>
                      <p className="font-bold text-gray-900 mt-1">{selectedRqm.category?.name || "N/A"}</p>
                    </div>

                    <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                      <p className="text-[10px] font-bold text-gray-400 uppercase">Sub-Category</p>
                      <p className="font-bold text-gray-900 mt-1">{selectedRqm.subCategory?.name || "N/A"}</p>
                    </div>

                    <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                      <p className="text-[10px] font-bold text-gray-400 uppercase">Product</p>
                      <p className="font-bold text-gray-900 mt-1">{selectedRqm.product?.name || "N/A"}</p>
                    </div>

                    <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                      <p className="text-[10px] font-bold text-gray-400 uppercase">Standard Unit</p>
                      <p className="font-bold text-gray-900 mt-1">{selectedRqm.priceAndQuantity?.standardizedUnit || "Kg"}</p>
                    </div>
                  </div>

                  {/* PRICING STATS */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-2">
                    <div className="p-4 bg-primaryColor/5 border border-primaryColor/15 rounded-xl text-xs space-y-1">
                      <span className="text-[10px] font-bold uppercase text-primaryColor">Offered Quantity</span>
                      <p className="text-lg font-extrabold text-gray-900">
                        {selectedRqm.priceAndQuantity?.quantity || 0} {selectedRqm.priceAndQuantity?.standardizedUnit || "Kg"}
                      </p>
                    </div>

                    <div className="p-4 bg-emerald-50 border border-emerald-200 rounded-xl text-xs space-y-1">
                      <span className="text-[10px] font-bold uppercase text-emerald-700">Rate per Unit</span>
                      <p className="text-lg font-extrabold text-emerald-700">
                        {formatCurrency(selectedRqm.pricing?.amount)}
                      </p>
                    </div>

                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl text-xs space-y-1">
                      <span className="text-[10px] font-bold uppercase text-blue-700">Total Deal Value</span>
                      <p className="text-lg font-extrabold text-blue-900">
                        {formatCurrency(selectedRqm.priceAndQuantity?.totalDealValue)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* PACKAGING & HOLDING SPECIFICATIONS CARD */}
                <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-2xs space-y-4">
                  <h3 className="text-sm font-extrabold text-gray-900 flex items-center gap-2 border-b border-gray-100 pb-3">
                    <FiPackage className="text-primaryColor" /> Packaging & Holding Period
                  </h3>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
                    <div className="space-y-3 bg-gray-50 p-4 rounded-xl border border-gray-100">
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase">Minimum Pack Weight</p>
                        <p className="font-extrabold text-gray-900 text-sm mt-0.5">
                          {selectedRqm.packSize?.weight || 1} {selectedRqm.packSize?.unit || selectedRqm.priceAndQuantity?.standardizedUnit || "kg"} per pack
                        </p>
                      </div>

                      <div className="pt-2 border-t border-gray-200">
                        <p className="text-[10px] font-bold text-gray-400 uppercase">Pack Dimensions (L x B x H)</p>
                        <p className="font-bold text-gray-900 mt-0.5">
                          {selectedRqm.packSize?.dimensions?.length || 0} x {selectedRqm.packSize?.dimensions?.breadth || 0} x{" "}
                          {selectedRqm.packSize?.dimensions?.height || 0} {selectedRqm.packSize?.dimensions?.measured || "cm"}
                        </p>
                      </div>
                    </div>

                    <div className="space-y-3 bg-gray-50 p-4 rounded-xl border border-gray-100">
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase flex items-center gap-1">
                          <FiCalendar size={12} className="text-primaryColor" /> Holding Period From
                        </p>
                        <p className="font-bold text-gray-900 mt-0.5">
                          {formatDate(selectedRqm.holdingPeriod?.from)}
                        </p>
                      </div>

                      <div className="pt-2 border-t border-gray-200">
                        <p className="text-[10px] font-bold text-gray-400 uppercase flex items-center gap-1">
                          <FiCalendar size={12} className="text-primaryColor" /> Holding Period To
                        </p>
                        <p className="font-bold text-gray-900 mt-0.5">
                          {formatDate(selectedRqm.holdingPeriod?.to)}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* PICKUP ADDRESS CARD */}
                <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-2xs space-y-3">
                  <h3 className="text-sm font-extrabold text-gray-900 flex items-center gap-2 border-b border-gray-100 pb-3">
                    <FiMapPin className="text-primaryColor" /> Pickup Location Details
                  </h3>

                  {selectedRqm.pickUpAddress ? (
                    <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 text-xs space-y-1">
                      <span className="font-bold text-primaryColor block">
                        {selectedRqm.pickUpAddress.addressType || "Office / Warehouse"}
                      </span>
                      <p className="font-semibold text-gray-900">{selectedRqm.pickUpAddress.addressLine1}</p>
                      {selectedRqm.pickUpAddress.addressLine2 && <p className="text-gray-600">{selectedRqm.pickUpAddress.addressLine2}</p>}
                      <p className="text-gray-600">
                        {selectedRqm.pickUpAddress.city}, {selectedRqm.pickUpAddress.state} - {selectedRqm.pickUpAddress.pincode}
                      </p>
                    </div>
                  ) : (
                    <p className="text-xs text-gray-400">No pickup address specified.</p>
                  )}
                </div>

                {/* REFERENCE MEDIA GALLERY */}
                <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-2xs space-y-4">
                  <h3 className="text-sm font-extrabold text-gray-900 flex items-center gap-2 border-b border-gray-100 pb-3">
                    <FiImage className="text-primaryColor" /> Reference Photos & Videos
                  </h3>

                  {/* IMAGES */}
                  {selectedRqm.referencePhotoAndVideo?.images?.length > 0 && (
                    <div className="space-y-2">
                      <p className="text-xs font-bold text-gray-700 uppercase flex items-center gap-1">
                        <FiImage size={14} /> Compulsory Product & Packaging Photos ({selectedRqm.referencePhotoAndVideo.images.length})
                      </p>
                      <div className="flex flex-wrap gap-3">
                        {selectedRqm.referencePhotoAndVideo.images.map((img, idx) => (
                          <div
                            key={idx}
                            onClick={() => setActiveMedia({ type: "image", url: img })}
                            className="relative w-28 h-28 rounded-xl border overflow-hidden cursor-pointer group bg-gray-50"
                          >
                            <img src={img} alt={`Ref ${idx + 1}`} className="w-full h-full object-cover group-hover:scale-105 transition" />
                            <span className="absolute bottom-1 left-1 bg-black/60 text-white px-1.5 py-0.5 rounded text-[9px] font-semibold">
                              {idx === 0 ? "Product 1" : idx === 1 ? "Product 2" : "Packaging 1"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* VIDEOS */}
                  {selectedRqm.referencePhotoAndVideo?.videos?.length > 0 && (
                    <div className="space-y-2 pt-3 border-t border-gray-100">
                      <p className="text-xs font-bold text-gray-700 uppercase flex items-center gap-1">
                        <FiVideo size={14} /> Compulsory Product & Packaging Videos ({selectedRqm.referencePhotoAndVideo.videos.length})
                      </p>
                      <div className="flex flex-wrap gap-3">
                        {selectedRqm.referencePhotoAndVideo.videos.map((vid, idx) => (
                          <div
                            key={idx}
                            onClick={() => setActiveMedia({ type: "video", url: vid })}
                            className="relative w-40 h-28 rounded-xl border bg-black overflow-hidden cursor-pointer group"
                          >
                            <video src={vid} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 transition" />
                            <span className="absolute bottom-1 left-1 bg-black/70 text-white px-1.5 py-0.5 rounded text-[9px] font-semibold">
                              {idx === 0 ? "Product Video" : "Packaging Video"}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* MEDIA PREVIEW MODAL */}
        {activeMedia && (
          <div
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-xs flex items-center justify-center p-4"
            onClick={() => setActiveMedia(null)}
          >
            <div className="relative max-w-4xl w-full max-h-[90vh] flex items-center justify-center">
              {activeMedia.type === "image" ? (
                <img src={activeMedia.url} alt="Enlarged preview" className="max-w-full max-h-[85vh] rounded-2xl object-contain shadow-2xl" />
              ) : (
                <video src={activeMedia.url} controls autoPlay className="max-w-full max-h-[85vh] rounded-2xl shadow-2xl" />
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SupplierRQMRequests;
